import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { timelineData, colorClasses } from '../components/timeline/TimelineData';
import { CountdownTimer } from '../components/timeline/CountdownTimer';

export const TimelinePage = () => {
  const { translations, language } = useLanguage();
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Get translations with English fallback
  const t = (key: string, fallback: string) => {
    return translations?.timeline?.[key] || fallback;
  };

  // Get the next upcoming deadline
  const now = new Date();
  const nextDeadline = timelineData.find(item => {
    const [day, month, year] = item.endDate.gregorian.split('/').map(Number);
    const deadline = new Date(year, month - 1, day);
    return deadline > now;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            <div className="p-2 sm:p-3 bg-primary-50 rounded-xl">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-primary-500" />
            </div>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl sm:text-3xl font-bold text-gray-900"
          >
            {t('title', 'Project Timeline')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-2 sm:mt-4 text-base sm:text-lg text-gray-600 px-4"
          >
            {t('subtitle', 'Key milestones and deadlines for the Hajj season')}
          </motion.p>
        </div>

        {/* Countdown Timer */}
        {nextDeadline && (
          <div className="mb-8 sm:mb-16 px-2">
            <div className="text-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                {t('timeRemaining', 'Time Remaining Until')} {' '}
                {language === 'ar' ? nextDeadline.title.ar : nextDeadline.title.en}
              </h2>
            </div>
            <CountdownTimer targetDate={nextDeadline.endDate.gregorian} />
          </div>
        )}

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line - Hidden on mobile, shown on larger screens */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-200 rounded-full" />

          {/* Timeline Items */}
          <div className="space-y-6 sm:space-y-20">
            {timelineData.map((item, index) => {
              const Icon = item.icon;
              const colors = colorClasses[item.color];
              const isExpanded = expandedItem === item.id;
              
              // Check if date is passed
              const [day, month, year] = item.endDate.gregorian.split('/').map(Number);
              const endDate = new Date(year, month - 1, day);
              const isPassed = endDate < now;
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className={`relative ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}
                >
                  {/* Content */}
                  <div className={`
                    relative w-full md:w-1/2 mb-6 md:mb-0 
                    ${index % 2 === 0 ? 'md:mr-auto md:pr-12' : 'md:ml-auto md:pl-12'}
                  `}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`
                        p-4 sm:p-6 rounded-xl shadow-sm border ${colors.border} ${colors.bg}
                        transition-all duration-300 hover:shadow-md
                        ${isPassed ? 'opacity-60' : ''}
                      `}
                    >
                      {/* Icon and Status */}
                      <div className="flex items-center justify-between mb-4">
                        <div className={`
                          w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center
                          ${colors.bg} ${colors.text}
                        `}>
                          <Icon size={20} className="sm:w-6 sm:h-6" />
                        </div>
                        <span className={`
                          px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium
                          ${isPassed 
                            ? 'bg-gray-100 text-gray-600' 
                            : 'bg-green-50 text-green-600'
                          }
                        `}>
                          {isPassed 
                            ? t('status.completed', 'Completed')
                            : t('status.upcoming', 'Upcoming')
                          }
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
                        {language === 'ar' ? item.title.ar : item.title.en}
                      </h3>

                      {/* Dates */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className={`${colors.text} sm:w-4 sm:h-4`} />
                          <span className="text-xs sm:text-sm text-gray-600">
                            {t('dates.start', 'Start:')} {' '}
                            <span className="font-medium">{item.startDate.gregorian}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className={`${colors.text} sm:w-4 sm:h-4`} />
                          <span className="text-xs sm:text-sm text-gray-600">
                            {t('dates.end', 'End:')} {' '}
                            <span className="font-medium">{item.endDate.gregorian}</span>
                          </span>
                        </div>
                      </div>

                      {/* Expand/Collapse Button */}
                      <button
                        onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                        className={`
                          mt-4 w-full flex items-center justify-center gap-2 
                          px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base
                          ${colors.bg} ${colors.text} hover:bg-opacity-75
                        `}
                      >
                        {isExpanded 
                          ? t('actions.showLess', 'Show Less')
                          : t('actions.showMore', 'Show More')
                        }
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t"
                        >
                          <div className="text-xs sm:text-sm text-gray-600 space-y-2">
                            <p>
                              {t('dates.label', 'Date')}: {' '}
                              {item.startDate.gregorian} - {item.endDate.gregorian}
                            </p>
                            <p className="mt-2">
                              {t('status.label', 'Status')}: {' '}
                              {isPassed 
                                ? t('status.phaseCompleted', 'This phase has been completed')
                                : t('status.phaseUpcoming', 'This phase is upcoming')
                              }
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>

                    {/* Timeline Dot - Hidden on mobile */}
                    <div className={`
                      hidden md:block absolute top-1/2 w-6 h-6 rounded-full ${colors.timeline}
                      transform -translate-y-1/2 shadow-lg
                      ${index % 2 === 0 ? '-right-3' : '-left-3'}
                      ${isPassed ? 'opacity-60' : ''}
                    `} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};